import { useSyncExternalStore, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { config } from '../config'
import { beginLogin, isAuthenticated, subscribe } from './session'

/**
 * The gate, and the whole of what the auth mode changes.
 *
 * In `none` — the local loop — this renders its children immediately.
 * That is not a bypass: the surface itself runs credential-free there,
 * with membership unbounded rather than empty, so there is genuinely
 * nothing to hold. Putting the hosted flow behind a flag is what stops
 * Cognito being a prerequisite for the first day of client work.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const location = useLocation()
  const signedIn = useSyncExternalStore(subscribe, isAuthenticated)

  if (signedIn) return <>{children}</>

  const returnTo = `${location.pathname}${location.search}`

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 460 }}>
        <Stack spacing={2}>
          <Typography variant="h1">Bluu CMS</Typography>
          <Typography color="text.secondary">
            Sign in with your Bluu CMS account.
          </Typography>
          {!config.cognito && (
            <Alert severity="error">
              Hosted sign-in is on, but no pool is configured. Set <code>VITE_COGNITO_DOMAIN</code>,{' '}
              <code>VITE_COGNITO_CLIENT_ID</code> and <code>VITE_COGNITO_REGION</code>.
            </Alert>
          )}
          <Button variant="contained" size="large" disabled={!config.cognito} onClick={() => void beginLogin(returnTo)}>
            Sign in
          </Button>
        </Stack>
      </Paper>
    </Box>
  )
}
